import React, { useState, useMemo } from "react";
import { Alert, Box, TextField, InputAdornment } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import MightyDataGrid from "../../../../components/organisms/MightyDataGrid";
import useDataGridActions from "../../../../hooks/useDataGridActions";
import organizationIdSelector from "../../../../atoms/organizationIdSelector";

const MealPlanDataGrid = ({
  mealPlans,
  onClick,
  onEdit,
  onDelete,
  onUpdate,
}) => {
  const theme = useTheme();
  const organizationId = useRecoilValue(organizationIdSelector);
  const { createEditDeleteActions } = useDataGridActions();
  const [searchTerm, setSearchTerm] = useState("");

  // Copy public meal plan URL to clipboard
  const copyMealPlanUrl = async (mealPlan) => {
    try {
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/meal-plan/${organizationId}/${mealPlan.id}`;

      await navigator.clipboard.writeText(shareUrl);

      // Show a brief notification
      const notification = document.createElement("div");
      notification.textContent = "Meal Plan URL copied to clipboard!";
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${theme.palette.primary.main};
        color: white;
        padding: 12px 20px;
        border-radius: 4px;
        z-index: 9999;
        font-family: Arial, sans-serif;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);

      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
    } catch (error) {
      console.error("Failed to copy URL:", error);
      // Fallback: show the URL in an alert
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/meal-plan/${organizationId}/${mealPlan.id}`;
      alert(`Copy this URL: ${shareUrl}`);
    }
  };

  // Handle meal plan sharing with privacy check
  const handleMealPlanShare = async (mealPlan) => {
    if (mealPlan.isPublic) {
      // If already public, just copy the URL
      await copyMealPlanUrl(mealPlan);
    } else {
      // If not public, ask if they want to make it public
      confirmAlert({
        title: "Make Meal Plan Public",
        message:
          "This meal plan is currently private. Would you like to make it public so it can be shared?",
        buttons: [
          {
            label: "Yes, Make Public",
            onClick: async () => {
              try {
                // Update the meal plan to be public
                if (onUpdate) {
                  await onUpdate({ ...mealPlan, isPublic: true });
                  // Copy the URL after making it public
                  await copyMealPlanUrl({ ...mealPlan, isPublic: true });
                } else {
                  console.error("onUpdate function not available");
                }
              } catch (error) {
                console.error("Failed to make meal plan public:", error);
                alert("Failed to make meal plan public. Please try again.");
              }
            },
          },
          {
            label: "Cancel",
            onClick: () => {},
          },
        ],
      });
    }
  };

  // Create custom actions for meal plans
  const createMealPlanActions = (mealPlan) => {
    const actions = [
      ...createEditDeleteActions({
        onEdit: () => onEdit(mealPlan),
        onDelete: () => onDelete(mealPlan),
        entityName: "Meal Plan",
        pageName: "Nutrition",
        tabName: "Meal Plans",
      }),
    ];

    // Always add share action - it will handle privacy internally
    actions.splice(1, 0, {
      key: "share",
      label: "Share",
      icon: <ShareIcon fontSize="small" />,
      onClick: () => handleMealPlanShare(mealPlan),
      show: true,
    });

    return actions;
  };
  const columns = React.useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Name",
        minWidth: 100,
        flex: 1,
        hideable: false,
      },
      {
        field: "isPublic",
        headerName: "Public?",
        type: "boolean",
        headerAlign: "center",
        align: "center",
        width: 85,
        renderCell: (params) => {
          if (!params.value) {
            return <Box>No</Box>;
          }

          return <Box style={{ color: theme.palette.secondary.main }}>Yes</Box>;
        },
      },
      {
        field: "kCals",
        headerName: "kCals",
        width: 150,
        valueGetter: (params) => {
          return params.row.totals?.totalkCals;
        },
      },
      {
        field: "proteins",
        headerName: "Proteins",
        width: 150,
        valueGetter: (params) => {
          return params.row.totals?.totalProteins;
        },
      },
      {
        field: "carbs",
        headerName: "Carbs",
        width: 150,
        valueGetter: (params) => {
          return params.row.totals?.totalCarbs;
        },
      },
      {
        field: "fats",
        headerName: "Fats",
        width: 150,
        valueGetter: (params) => {
          return params.row.totals?.totalFats;
        },
      },
      {
        field: "fiber",
        headerName: "Fiber",
        width: 150,
        valueGetter: (params) => {
          return params.row.totals?.totalFiber;
        },
      },
      {
        field: "recipes",
        headerName: "Recipes",
        width: 150,
        valueGetter: (params) => {
          return params.row.selectedRecipes.length;
        },
      },
    ];
  }, [theme.palette.secondary.main]);

  // Filter meal plans based on search term
  const filteredMealPlans = useMemo(() => {
    if (!searchTerm) return mealPlans;

    return mealPlans.filter((mealPlan) =>
      mealPlan.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [mealPlans, searchTerm]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Shared links only work for meal plans marked <strong>Public</strong>. Use
        Share on a row to make a plan public and copy its link, or set Public?
        to Yes before sharing.
      </Alert>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <TextField
          placeholder="Search meal plans by name..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
      </Box>
      <MightyDataGrid
        entityName="mealPlans"
        columns={columns}
        rows={filteredMealPlans}
        onRowClick={onClick}
        showActions={true}
        actions={createMealPlanActions}
      />
    </Box>
  );
};

export default MealPlanDataGrid;
